"use client";
import { useCurrency } from "@/app/providers/currencyContext";
import DisableOverlay from "@/components/disableOverlay/disableOverlay";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { Modifier } from "@/dto/modifier";
import { ModifierGroup } from "@/dto/modifierGroup";
import { getPrice } from "@/utils/getPrice";
import { lightenColor } from "@/utils/lightenColor";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ModalModifierSelection } from "./modalModifierSelection";
import { useMerchant } from "@/app/providers/merchantProvider";
import { hasEmptyBusinessHours } from "@/utils/merchantUtils";

export const MAX_QUANTITY = 100;
export const MIN_QUANTITY = 1;

export const ServiceModalModifierSelection = ({
  isCheckoutEnabled,
  merchantName,
  productImage,
  service,
  formatDuration,
  onShowEmployeeSelection,
  modifiers,
  selectedModifiers,
  setSelectedModifiers,
  menu,
  setFinalPrice,
  isAcceptingAppointments,
  merchantColor,
  quantity,
  setQuantity,
  isAddtocartClicked,
  setIsAddtocartClicked,
  imageRef,
  scrollContainerRef,
  titleId,
  subtitleId,
}: any) => {
  const t = useTranslations("ServiceModalModifierSelection");
  const { merchant } = useMerchant();
  const tServiceSummary = useTranslations("ServiceSummary");
  const tDuration = useTranslations("SingleProductDescriptionFocusedDuration");
  const { format } = useCurrency();

  const isPerUnitService: boolean = service?.priceType === "PER_UNIT";
  const headingRef = useRef<HTMLHeadingElement>(null);
  const filteredModifierGroups: ModifierGroup[] = menu.modifierGroups
    ? (Object.values(menu.modifierGroups).filter((group: any) =>
        service.modifierGroupIds.includes(group.id)
      ) as ModifierGroup[])
    : [];

  const selectableModifierGroups: ModifierGroup[] =
    filteredModifierGroups.length > 0
      ? filteredModifierGroups
          .map((group: ModifierGroup) => ({
            ...group,
            modifiers: modifiers.filter(
              (mod: Modifier) => mod.groupId === group.id
            ),
            isSingleSelection:
              group.minRequired === 1 && group.maxAllowed === 1,
          }))
          .filter(
            (group: ModifierGroup) =>
              group.modifiers?.length > 0 &&
              group.modifiers?.some((modifier: Modifier) => modifier.available)
          )
      : [];

  const allModifiers = menu.modifiers;

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    setIsAddtocartClicked(false);
    const modifier: Modifier | undefined = modifiers.find(
      (mod: Modifier) => mod.id === modifierId
    );
    if (!modifier) {
      return;
    }

    const group: ModifierGroup = selectableModifierGroups.find(
      (g: ModifierGroup) => g.id === groupId
    ) as ModifierGroup;

    if (!group) return;

    setSelectedModifiers((prev: any) => {
      const newSelectedModifiers = { ...prev };
      const groupSelections: { [key: string]: number } = {
        ...(newSelectedModifiers[groupId] ?? {}),
      };

      if (isSingleSelection) {
        newSelectedModifiers[groupId] = { [modifierId]: 1 };
        return newSelectedModifiers;
      }

      const isSelected = groupSelections[modifierId];

      if (isSelected) {
        delete groupSelections[modifierId];
      } else {
        const totalGroupQuantity = Object.values(groupSelections).reduce(
          (acc: number, quantity: number) => acc + quantity,
          0
        );
        if (totalGroupQuantity < group.maxAllowed) {
          groupSelections[modifierId] = 1;
        }
      }
      newSelectedModifiers[groupId] = groupSelections;
      return newSelectedModifiers;
    });
  };

  const handleModifierQuantityChange = (
    groupId: string,
    modifierId: string,
    newQuantity: number
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup = selectableModifierGroups.find(
      (g: ModifierGroup) => g.id === groupId
    ) as ModifierGroup;
    if (!group) return;

    if (newQuantity === 0) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = { ...prev };
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          delete newSelectedModifiers[groupId][modifierId];
        }
        return newSelectedModifiers;
      });
      return;
    }

    const groupSelections = selectedModifiers[groupId] ?? {};
    const otherModifiersQuantity: number = Object.keys(groupSelections).reduce(
      (acc, mId) => {
        if (mId !== modifierId) {
          return acc + groupSelections[mId];
        }
        return acc;
      },
      0
    );

    const availableQuantity: number = Math.min(
      group.maxAllowed - otherModifiersQuantity,
      99
    );

    if (newQuantity >= 1 && newQuantity <= availableQuantity) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = JSON.parse(JSON.stringify(prev));
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          newSelectedModifiers[groupId][modifierId] = newQuantity;
        }
        return newSelectedModifiers;
      });
    }
  };

  const [isMinusDisabled, setIsMinusDisabled] = useState<boolean>(
    quantity ? quantity <= MIN_QUANTITY : true
  );
  const [isPlusDisabled, setIsPlusDisabled] = useState<boolean>(
    quantity ? quantity >= MAX_QUANTITY : false
  );

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= Number.MAX_SAFE_INTEGER) {
      setQuantity(newQuantity);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (headingRef.current) {
        headingRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (quantity === undefined) {
      setIsMinusDisabled(false); // Allow decrementing to MIN_QUANTITY
      setIsPlusDisabled(false);
    } else {
      setIsMinusDisabled(quantity <= MIN_QUANTITY);
      setIsPlusDisabled(quantity >= MAX_QUANTITY);
    }
  }, [quantity]);

  let disableBookButton: boolean =
    service?.available === false || !isAcceptingAppointments || hasEmptyBusinessHours(merchant);

  return (
    <div
      ref={scrollContainerRef}
      className="flex flex-col sm:flex-row-reverse w-full h-full overflow-y-auto sm:overflow-y-hidden"
    >
      {productImage && (
        <div className="flex flex-1 flex-shrink-0 sm:max-h-full sm:w-[50%]">
          <Image
            aria-hidden={true}
            ref={imageRef}
            width={450}
            height={576}
            alt={service.name ?? t("serviceNameAlt")}
            src={productImage.source}
            className="object-cover object-center w-full h-full"
          />
        </div>
      )}
      <div
        className={`flex flex-1 flex-col sm:min-h-[260px] w-full ${
          productImage ? "sm:w-[50%]" : "sm:w-[530px]"
        } relative md:h-full sm:h-auto sm:overflow-y-hidden`}
      >
        <DisableOverlay
          merchantName={merchantName}
          isCheckoutEnabled={isCheckoutEnabled}
        >
          <div className="flex flex-col px-6 pt-[14px] sm:pt-6 pb-3 bg-white sticky top-0 shadow-modalStickyHeader">
            <h2
              id={titleId}
              ref={headingRef}
              tabIndex={-1}
              aria-describedby={subtitleId}
              data-testid={applyDataTestId(pom.itemModal.name)}
              className="sm:pt-4 text-4xl text-[32px] font-medium text-[#373737] whitespace-normal overflow-hidden text-ellipsis focus:outline-none"
            >
              {service.name}
            </h2>
            <p
              data-testid={applyDataTestId(pom.serviceModal.price)}
              className="text-[15px] font-medium pt-2 sm:pt-4"
            >
              {isPerUnitService
                ? tServiceSummary("servicePricePerUnit", {
                    price: format(service.price),
                    unitName: service.unitName,
                  })
                : tServiceSummary("servicePrice", {
                    price: format(service.price),
                  })}{" "}
              {service.serviceItem && (
                <span className="">
                  &nbsp;&bull;&nbsp;{" "}
                  {formatDuration(service.serviceItem.duration) ??
                    `0 ${tDuration("min")}`}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col px-6 sm:max-h-[420px] overflow-y-visible sm:overflow-y-auto mb-8 pb-4 sm:pb-[21px]">
            <div className="py-4">
              <div
                id={subtitleId}
                data-testid={applyDataTestId(pom.serviceModal.description)}
                className="text-gray-500 break-words whitespace-pre-wrap"
              >
                {service.description && <p>{service.description}</p>}
              </div>
            </div>
            {selectableModifierGroups.length > 0 && (
              <ModalModifierSelection
                {...{
                  selectableModifierGroups,
                  selectedModifiers,
                  setSelectedModifiers,
                  handleModifierChange,
                  handleModifierQuantityChange,
                  merchantColor,
                  isAddtocartClicked,
                }}
              />
            )}
          </div>
          {isPerUnitService && (
            <PerUnitBottomButtons
              {...{
                disableBookButton,
                merchantColor,
                setFinalPrice,
                service,
                selectedModifiers,
                onShowEmployeeSelection,
                isAcceptingAppointments,
                quantity,
                perUnitName: service?.unitName,
                isMinusDisabled,
                isPlusDisabled,
                handleQuantityChange,
                filteredModifierGroups,
                allModifiers,
                isPerUnitService,
              }}
            />
          )}
          {!isPerUnitService && (
            <FixedPriceTypeBottomButtons
              {...{
                disableBookButton,
                merchantColor,
                setFinalPrice,
                service,
                selectedModifiers,
                onShowEmployeeSelection,
                isAcceptingAppointments,
                filteredModifierGroups,
                allModifiers,
              }}
            />
          )}
        </DisableOverlay>
      </div>
    </div>
  );
};

const PerUnitBottomButtons = ({
  disableBookButton,
  merchantColor,
  setFinalPrice,
  service,
  selectedModifiers,
  onShowEmployeeSelection,
  isAcceptingAppointments,
  quantity,
  handleQuantityChange,
  perUnitName,
  isMinusDisabled,
  isPlusDisabled,
  filteredModifierGroups,
  allModifiers,
  isPerUnitService,
}: any) => {
  const { format } = useCurrency();
  const tEmployeeSelection = useTranslations("ServiceModalEmployeeSelection");
  const t = useTranslations("ServiceModalModifierSelection");
  return (
    <div className="fixed sm:absolute bottom-0 left-0 w-full sm:h-[64px] flex flex-col sm:flex-row">
      <div
        style={{
          backgroundColor: lightenColor(merchantColor, 90),
        }}
        className="bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium sm:w-2/5 flex flex-row rounded-bl-md h-[64px]"
      >
        <QuantityStepper
          handleQuantityChange={handleQuantityChange}
          isModalQuantity={true}
          max={MAX_QUANTITY}
          min={MIN_QUANTITY}
          quantity={quantity}
          isMinusDisabled={isMinusDisabled}
          disablePlus={isPlusDisabled}
          merchantColor={merchantColor}
          perUnitName={perUnitName}
          adaName={service?.name}
        />
      </div>
      <button
        data-testid={applyDataTestId(pom.serviceModal.bookButton)}
        style={{
          backgroundColor: disableBookButton ? "#C7C7C7" : merchantColor,
        }}
        className="flex sm:w-3/5 w-full h-[64px] bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium"
        onClick={() => {
          setFinalPrice(
            getPrice({
              item: service,
              filteredModifierGroups,
              allModifiers,
              selectedModifiers,
              quantity,
              isPerUnitService,
            })
          );
          onShowEmployeeSelection();
        }}
        disabled={disableBookButton}
      >
        {service?.available === false || !isAcceptingAppointments ? (
          <div className="text-[#373737] text-[15px]">
            <p>{t("serviceUnavailable")}</p>
          </div>
        ) : (
          <div className="flex w-full h-full flex-row px-6 items-center justify-between">
            <p>{tEmployeeSelection("book")}&nbsp;</p>
            <p>
              {format(
                getPrice({
                  item: service,
                  filteredModifierGroups,
                  allModifiers,
                  selectedModifiers,
                  quantity,
                  isPerUnitService,
                })
              )}
            </p>
          </div>
        )}
      </button>
    </div>
  );
};

const FixedPriceTypeBottomButtons = ({
  disableBookButton,
  merchantColor,
  setFinalPrice,
  service,
  selectedModifiers,
  onShowEmployeeSelection,
  isAcceptingAppointments,
  filteredModifierGroups,
  allModifiers,
}: any) => {
  const { format } = useCurrency();
  const tEmployeeSelection = useTranslations("ServiceModalEmployeeSelection");
  const t = useTranslations("ServiceModalModifierSelection");
  return (
    <button
      data-testid={applyDataTestId(pom.serviceModal.bookButton)}
      style={{
        backgroundColor: disableBookButton ? "#C7C7C7" : merchantColor,
      }}
      className="fixed sm:absolute bottom-0 clover-button-primary left-0 w-full h-[64px] flex flex-col sm:flex-row bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium rounded-bl-none sm:rounded-bl-md"
      onClick={() => {
        setFinalPrice(
          getPrice({
            item: service,
            filteredModifierGroups,
            allModifiers,
            selectedModifiers,
            quantity: 1,
          })
        );
        onShowEmployeeSelection();
      }}
      disabled={disableBookButton}
    >
      {service?.available === false || !isAcceptingAppointments ? (
        <div className="text-[#373737] text-[15px]">
          <p>{t("serviceUnavailable")}</p>
        </div>
      ) : (
        <div className="flex w-full h-full flex-row px-6 items-center justify-between">
          <span>{tEmployeeSelection("book")}&nbsp;</span>
          <span>
            {format(
              getPrice({
                item: service,
                filteredModifierGroups,
                allModifiers,
                selectedModifiers,
                quantity: 1,
              })
            )}
          </span>
        </div>
      )}
    </button>
  );
};
